import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Send, Users as UsersIcon } from 'lucide-react';
import { api } from '../context/AuthContext';
import { toast } from 'sonner';

export default function AdminMessagesTab() {
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const endRef = useRef(null);

  const fetchThreads = async () => {
    setLoadingThreads(true);
    try {
      const { data } = await api.get('/api/messages/threads');
      setThreads(data || []);
    } catch { toast.error('Failed to load conversations'); }
    finally { setLoadingThreads(false); }
  };

  const fetchThread = async (parentId) => {
    setLoadingThread(true);
    try {
      const { data } = await api.get(`/api/messages/thread/${parentId}`);
      setMessages(data || []);
    } catch { toast.error('Failed to load thread'); }
    finally { setLoadingThread(false); }
  };

  useEffect(() => { fetchThreads(); }, []);
  useEffect(() => {
    if (selected) fetchThread(selected.parent_id);
  }, [selected?.parent_id]);
  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // SSE subscription — refresh threads + append to active thread on new message
  useEffect(() => {
    const token = localStorage.getItem('craven_access_token');
    const base = `${process.env.REACT_APP_BACKEND_URL}/api/messages/stream`;
    const url = token ? `${base}?access_token=${encodeURIComponent(token)}` : base;
    const es = new EventSource(url, { withCredentials: true });
    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type !== 'new_message') return;
        const m = data.message;
        // If this matches the currently-open thread and is not a dupe, append
        if (selected && m.parent_id === selected.parent_id) {
          setMessages((prev) => {
            if (prev.some((x) => x.id === m.id)) return prev;
            return [...prev, m];
          });
          // Mark admin side as read if we have the thread open
          if (m.sender_role === 'parent') {
            api.get(`/api/messages/thread/${selected.parent_id}`).catch(() => {});
          }
        }
        // Always refresh threads list (updates unread counts + last message preview)
        fetchThreads();
        if (m.sender_role === 'parent' && (!selected || m.parent_id !== selected.parent_id)) {
          toast.info(`New message from ${m.sender_name || 'a parent'}`);
        }
      } catch { /* ignore */ }
    };
    es.onerror = () => { /* auto-reconnects */ };
    return () => { es.close(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.parent_id]);

  const send = async (e) => {
    e?.preventDefault();
    const v = text.trim();
    if (!v || !selected || sending) return;
    setSending(true);
    try {
      await api.post('/api/messages', { parent_id: selected.parent_id, text: v });
      setText('');
      await fetchThread(selected.parent_id);
      fetchThreads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  return (
    <div data-testid="admin-messages-tab">
      <div className="mb-6">
        <h2 className="font-fredoka text-xl font-bold text-gray-800">Messages</h2>
        <p className="font-nunito text-sm text-gray-500">Chat with parents — one thread per family.</p>
      </div>

      <div className="grid md:grid-cols-[320px_1fr] gap-4 md:h-[650px]">
        {/* Thread list */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col">
          <div className="p-4 border-b border-slate-100 flex items-center gap-2">
            <UsersIcon className="w-4 h-4 text-slate-400" />
            <span className="font-nunito font-semibold text-slate-700 text-sm">
              {threads.length} parent{threads.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="overflow-y-auto flex-1 min-h-[300px]">
            {loadingThreads ? (
              <div className="flex justify-center py-10">
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  className="w-6 h-6 border-2 border-[#FD5E53] border-t-transparent rounded-full" />
              </div>
            ) : threads.length === 0 ? (
              <p className="text-center py-10 text-slate-400 font-nunito text-sm">No parents yet.</p>
            ) : (
              threads.map((t) => {
                const active = selected?.parent_id === t.parent_id;
                return (
                  <button
                    key={t.parent_id}
                    onClick={() => setSelected(t)}
                    className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${
                      active ? 'bg-[#FFEDD5]/50' : ''
                    }`}
                    data-testid={`thread-${t.parent_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FDE047] to-[#F59E0B] flex items-center justify-center font-fredoka font-semibold text-white flex-shrink-0">
                        {t.parent_name?.[0]?.toUpperCase() || 'P'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-fredoka font-medium text-slate-800 text-sm truncate">{t.parent_name}</p>
                          <span className="font-nunito text-[11px] text-slate-400 flex-shrink-0">
                            {formatTime(t.last_at)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-2 mt-0.5">
                          <p className="font-nunito text-xs text-slate-500 truncate">
                            {t.last_message || <span className="italic text-slate-400">No messages yet</span>}
                          </p>
                          {t.unread_count > 0 && (
                            <span className="bg-[#FD5E53] text-white text-[10px] font-bold rounded-full px-1.5 min-w-[20px] h-5 flex items-center justify-center flex-shrink-0">
                              {t.unread_count}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Thread view */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col min-h-[500px]">
          {!selected ? (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-[#FFE4E6] flex items-center justify-center mb-3">
                <MessageCircle className="w-8 h-8 text-[#FD5E53]" />
              </div>
              <p className="font-fredoka text-lg text-slate-800 mb-1">Pick a conversation</p>
              <p className="font-nunito text-sm text-slate-500">Select a parent on the left to start messaging.</p>
            </div>
          ) : (
            <>
              <div className="p-4 border-b border-slate-100 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FDE047] to-[#F59E0B] flex items-center justify-center font-fredoka font-semibold text-white">
                  {selected.parent_name?.[0]?.toUpperCase() || 'P'}
                </div>
                <div>
                  <p className="font-fredoka font-semibold text-slate-800">{selected.parent_name}</p>
                  <p className="font-nunito text-xs text-slate-500">{selected.parent_email}</p>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 bg-[#FDFBF7]" data-testid="admin-thread-messages">
                {loadingThread ? (
                  <div className="flex justify-center py-10">
                    <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      className="w-6 h-6 border-2 border-[#FD5E53] border-t-transparent rounded-full" />
                  </div>
                ) : messages.length === 0 ? (
                  <p className="text-center py-10 text-slate-400 font-nunito text-sm">
                    No messages yet — say hi!
                  </p>
                ) : (
                  <div className="space-y-3">
                    {messages.map((m) => {
                      const isAdmin = m.sender_role === 'admin';
                      return (
                        <div key={m.id} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                            isAdmin
                              ? 'bg-[#38BDF8] text-white rounded-br-md'
                              : 'bg-white text-slate-800 rounded-bl-md shadow-sm border border-slate-100'
                          }`}>
                            <p className="font-nunito text-sm whitespace-pre-wrap">{m.text}</p>
                            <p className={`font-nunito text-[10px] mt-1 ${isAdmin ? 'text-white/70' : 'text-slate-400'}`}>
                              {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={endRef} />
                  </div>
                )}
              </div>

              <form onSubmit={send} className="p-3 border-t border-slate-100 flex gap-2">
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Reply to parent..."
                  maxLength={2000}
                  disabled={sending}
                  className="flex-1 rounded-full bg-slate-50 border-0 px-4 py-2.5 text-sm font-nunito focus:outline-none focus:ring-2 focus:ring-[#38BDF8]"
                  data-testid="admin-chat-input"
                />
                <button
                  type="submit"
                  disabled={!text.trim() || sending}
                  className="w-10 h-10 rounded-full bg-[#38BDF8] hover:bg-[#0EA5E9] text-white flex items-center justify-center disabled:opacity-50 transition-colors flex-shrink-0"
                  data-testid="admin-chat-send"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
